import type { User } from '@auth0/auth0-spa-js';

import type { SessionUser } from '@shared/types/session';

/// Auth0 Action でアプリ固有クレームを発行する際の prefix。
/// Pulumi の `benpal-db:auth0CustomClaimPrefix` と一致させる。
export const AUTH0_CUSTOM_CLAIM_PREFIX = 'https://app.benpal-legal.com';

/// role 配列 (`["super_admin"]` / `["member"]` 等) を載せるカスタムクレーム名。
export const AUTH0_ROLES_CLAIM = `${AUTH0_CUSTOM_CLAIM_PREFIX}/roles`;

/// ログイン中組織 (`event.organization` オブジェクト全体) を載せるカスタムクレーム名。
export const AUTH0_ORGANIZATION_CLAIM = `${AUTH0_CUSTOM_CLAIM_PREFIX}/organization`;

const KNOWN_ROLES = [
  'super_admin',
  'org_admin',
  'member',
  'readonly',
] as const satisfies readonly SessionUser['role'][];

function isAppRole(value: unknown): value is SessionUser['role'] {
  return typeof value === 'string' && (KNOWN_ROLES as readonly string[]).includes(value);
}

/// Auth0 user から最も特権の高いロールを取り出す。
/// 優先度 super_admin > org_admin > member > readonly。クレーム未設定や未知のロールしか
/// 無い場合は readonly に落とす (権限拒否側に倒す)。
///
/// 移行期の後方互換: legacy slug `"admin"` を `super_admin` として受理する
/// (backend `pick_role` と同じ。改名前に発行されたトークン対策。cleanup PR #402 で除去予定)。
export function pickAppRole(user: User): SessionUser['role'] {
  const raw = (user as Record<string, unknown>)[AUTH0_ROLES_CLAIM];
  if (!Array.isArray(raw)) return 'readonly';
  const roles = raw
    .map((r) => (r === 'admin' ? 'super_admin' : r))
    .filter(isAppRole);
  if (roles.includes('super_admin')) return 'super_admin';
  if (roles.includes('org_admin')) return 'org_admin';
  if (roles.includes('member')) return 'member';
  return 'readonly';
}

/// Auth0 user から「ログイン中組織」の id を取り出す。post-login Action が
/// `event.organization` オブジェクト全体を注入するので id は `.id` に居る
/// (backend `resolve_organization_id` と同形)。クレーム未設定 / 形不正 / 空 id は null。
/// super_admin は org 不要、org_admin で null の場合は org スコープ操作を backend が 403 にする。
export function pickOrgId(user: User): string | null {
  const raw = (user as Record<string, unknown>)[AUTH0_ORGANIZATION_CLAIM];
  if (raw && typeof raw === 'object' && 'id' in raw) {
    const id = (raw as Record<string, unknown>).id;
    if (typeof id === 'string' && id.length > 0) return id;
  }
  return null;
}
